import { DEFAULT_STAFF_PERMISSIONS, type OperatorPermission } from '../permissions';

const BASE = import.meta.env.VITE_API_BASE_URL ?? '/v1';

const ACCESS_KEY = 'parkingpay.admin.access';
const REFRESH_KEY = 'parkingpay.admin.refresh';
const OPERATOR_KEY = 'parkingpay.admin.operator';

export interface Operator {
  id: string;
  name: string;
  email: string;
  organizationId: string;
  role: 'owner' | 'staff';
  permissions: OperatorPermission[];
  lotIds: string[] | null;
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: Record<string, unknown>,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export const session = {
  get accessToken() {
    return localStorage.getItem(ACCESS_KEY);
  },
  get refreshToken() {
    return localStorage.getItem(REFRESH_KEY);
  },
  get operator(): Operator | null {
    const raw = localStorage.getItem(OPERATOR_KEY);
    if (!raw) return null;
    const operator = JSON.parse(raw) as Operator;
    if (!Array.isArray(operator.permissions)) {
      operator.permissions = operator.role === 'staff' ? DEFAULT_STAFF_PERMISSIONS : [];
    }
    if (operator.lotIds === undefined) operator.lotIds = operator.role === 'owner' ? null : [];
    return operator;
  },
  save(data: { accessToken: string; refreshToken: string; operator: Operator }) {
    localStorage.setItem(ACCESS_KEY, data.accessToken);
    localStorage.setItem(REFRESH_KEY, data.refreshToken);
    localStorage.setItem(OPERATOR_KEY, JSON.stringify(data.operator));
  },
  clear() {
    localStorage.removeItem(ACCESS_KEY);
    localStorage.removeItem(REFRESH_KEY);
    localStorage.removeItem(OPERATOR_KEY);
  },
};

async function refresh(): Promise<boolean> {
  const token = session.refreshToken;
  if (!token) return false;
  const response = await fetch(`${BASE}/auth/refresh`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ refreshToken: token }),
  });
  if (!response.ok) {
    session.clear();
    return false;
  }
  session.save(await response.json());
  return true;
}

async function request<T>(path: string, init: RequestInit = {}, retry = true): Promise<T> {
  const token = session.accessToken;
  const response = await fetch(`${BASE}${path}`, {
    ...init,
    headers: {
      'content-type': 'application/json',
      ...(token ? { authorization: `Bearer ${token}` } : {}),
      ...init.headers,
    },
  });

  if (response.status === 401 && retry && (await refresh())) {
    return request<T>(path, init, false);
  }

  if (response.status === 204) return undefined as T;

  const contentType = response.headers.get('content-type') ?? '';
  if (!contentType.includes('application/json')) {
    const text = await response.text();
    if (!response.ok) throw new ApiError(response.status, 'unexpected_response', text.slice(0, 200));
    return text as T;
  }

  const payload = await response.json();
  if (!response.ok) {
    const error = payload?.error;
    throw new ApiError(
      response.status,
      error?.code ?? 'unknown_error',
      error?.message ?? `Request failed with status ${response.status}`,
      error?.details,
    );
  }
  return payload as T;
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'POST', body: body === undefined ? undefined : JSON.stringify(body) }),
  patch: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'PATCH', body: body === undefined ? undefined : JSON.stringify(body) }),
  delete: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
  upload: <T>(path: string, file: File) =>
    request<T>(path, {
      method: 'POST',
      body: file,
      headers: { 'content-type': file.type },
    }),
  /** CSV export goes through the same auth path but keeps the raw body. */
  download: async (path: string, filename: string) => {
    const token = session.accessToken;
    const response = await fetch(`${BASE}${path}`, {
      headers: token ? { authorization: `Bearer ${token}` } : {},
    });
    if (!response.ok) throw new ApiError(response.status, 'export_failed', 'CSV export failed');
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
  },
};

export const usd = (cents: number) => {
  const sign = cents < 0 ? '-' : '';
  const abs = Math.abs(cents);
  return `${sign}$${Math.floor(abs / 100)}.${String(abs % 100).padStart(2, '0')}`;
};

export const parseUsd = (input: string): number => Math.round(Number(input.replace(/[^0-9.]/g, '')) * 100);

export const minuteLabel = (minute: number) =>
  `${String(Math.floor(minute / 60)).padStart(2, '0')}:${String(minute % 60).padStart(2, '0')}`;

/**
 * Durations are worded by the caller's translator, never hard-coded — the
 * console is bilingual and this string ends up inside a rate table header.
 */
export const durationLabel = (
  minutes: number,
  t: (key: string, opts?: Record<string, unknown>) => string,
) => {
  if (minutes === 1440) return t('units.fullDay');
  if (minutes < 60) return t('units.minutes', { count: minutes });
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m === 0 ? t('units.hours', { count: h }) : t('units.hoursMinutes', { hours: h, minutes: m });
};

export const clock = (iso: string) =>
  new Date(iso).toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit', hour12: false });

export const dateLabel = (iso: string) =>
  new Date(iso).toLocaleDateString('zh-CN', { month: 'numeric', day: 'numeric' });

export const countdown = (seconds: number) => {
  const s = Math.max(0, seconds);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${Math.floor(s / 3600)}:${pad(Math.floor((s % 3600) / 60))}:${pad(s % 60)}`;
};
